import React, { useEffect, useMemo, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Store, Users, TrendingUp, Coffee, Map, Check, Info, MessageSquare, Mail, Calendar, MapPin, ShoppingBag, ChevronRight, Truck, Maximize2, X } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { ExpositorRegistrationModal } from '../components/ExpositorRegistrationModal';
import { formatCOP, priceBreakdown } from '../utils/format';
import { supabase } from '../utils/supabase';
type StandStatus = 'available' | 'reserved' | 'sold';
type PavilionType = 'comercial' | 'foodtrucks';
interface Stand {
  id: string;
  status: StandStatus;
  size: string;
  price: number;
  isCorner?: boolean;
  isLarge?: boolean;
  type: PavilionType;
  standType?: 'AAA' | 'AA' | 'A';
  frentes?: string;
  row?: number;
  col?: number;
}
// Stand type definitions for commercial pavilion (36 total)
const AAA_STANDS = [13, 24];
const AA_STANDS = [1, 6, 7, 12, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 25, 30, 31, 36];
// Toldos availability
const TOLDOS_TOTAL = 20;
// Cute food truck icon — proper truck with awning, serving window, and wheels
function FoodTruckIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden data-id="element-2020" data-mp="2ivhn2nqsxzg">
      {/* Striped awning */}
      <path d="M10 14 L54 14 L50 8 L14 8 Z" fill="currentColor" opacity="0.85" data-id="element-2021" data-mp="v715vze09541" />
      <path d="M18 8 L18 14 M26 8 L26 14 M34 8 L34 14 M42 8 L42 14 M50 8 L50 14" stroke="white" strokeWidth="0.8" opacity="0.4" data-id="element-2022" data-mp="h7jfxgoz3lt3" />

      {/* Truck body */}
      <rect x="6" y="14" width="44" height="22" rx="2" fill="currentColor" data-id="element-2023" data-mp="31wpiotmpjdfo" />

      {/* Cab on the right */}
      <path d="M50 18 L58 22 L58 36 L50 36 Z" fill="currentColor" opacity="0.75" data-id="element-2024" data-mp="36x3g7hqon4zg" />

      {/* Cab window */}
      <path d="M51 21 L56.5 23.5 L56.5 28 L51 28 Z" fill="white" opacity="0.45" data-id="element-2025" data-mp="1chy0hbsh5om4" />

      {/* Serving window */}
      <rect x="12" y="18" width="22" height="12" rx="1" fill="white" opacity="0.85" data-id="element-2026" data-mp="12pihcb5fytzn" />

      {/* Counter */}
      <rect x="10" y="30" width="26" height="2" fill="white" opacity="0.55" data-id="element-2027" data-mp="ymm4z26hvu45" />

      {/* Wheels */}
      <circle cx="18" cy="38" r="4" fill="#1a1a1a" data-id="element-2028" data-mp="tmzlwc3zjjnf" />
      <circle cx="18" cy="38" r="1.6" fill="white" opacity="0.8" data-id="element-2029" data-mp="ophjl4kh42oe" />
      <circle cx="48" cy="38" r="4" fill="#1a1a1a" data-id="element-2030" data-mp="jqmpv7kywtpi" />
      <circle cx="48" cy="38" r="1.6" fill="white" opacity="0.8" data-id="element-2031" data-mp="jrfzqhf83zbv" />
    </svg>;
}
export function ExpositoresPage() {
  const [activeTab, setActiveTab] = useState<PavilionType>('comercial');
  const [ftSubTab, setFtSubTab] = useState<'foodtrucks' | 'toldos'>('foodtrucks');
  const [selectedStand, setSelectedStand] = useState<Stand | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [registrationType, setRegistrationType] = useState<'comercial' | 'foodtruck' | 'toldo'>('comercial');
  const [ftWidthStr, setFtWidthStr] = useState('5');
  const [ftLengthStr, setFtLengthStr] = useState('3');
  const [toldosQuantity, setToldosQuantity] = useState(0);
  const [occupiedStands, setOccupiedStands] = useState<Record<string, 'paid' | 'pending_payment'>>({});
  const [realToldosSold, setRealToldosSold] = useState(0);
  useEffect(() => {
    // Status synonyms — keep the front tolerant of different webhooks / dashboards
    // that write 'paid' OR 'approved' (Wompi's word) for the same outcome.
    const SOLD_STATUSES = ['paid', 'approved', 'complete', 'completed'];
    const RESERVED_STATUSES = ['pending_payment', 'pending'];
    const ALL_BLOCKING = [...SOLD_STATUSES, ...RESERVED_STATUSES];
    const normalize = (raw: string | null | undefined): 'paid' | 'pending_payment' | null => {
      if (!raw) return null;
      const s = raw.toLowerCase().trim();
      if (SOLD_STATUSES.includes(s)) return 'paid';
      if (RESERVED_STATUSES.includes(s)) return 'pending_payment';
      return null;
    };
    const fetchAvailability = async () => {
      // Fetch stands — case-insensitive match on any blocking status
      const {
        data: standsData
      } = await supabase.from('expositor_reservations').select('stand_id, status').not('stand_id', 'is', null);
      if (standsData) {
        const occupied: Record<string, 'paid' | 'pending_payment'> = {};
        standsData.forEach(row => {
          const normalized = normalize(row.status);
          if (row.stand_id && normalized) {
            occupied[row.stand_id] = normalized;
          }
        });
        setOccupiedStands(occupied);
      }
      // Fetch toldos
      const {
        data: toldosData
      } = await supabase.from('toldos_reservations').select('quantity, status');
      if (toldosData) {
        const sold = toldosData.reduce((sum, row) => {
          const normalized = normalize(row.status);
          return normalized ? sum + (row.quantity || 0) : sum;
        }, 0);
        setRealToldosSold(sold);
      }
    };
    fetchAvailability();
    // Realtime subscription — refresh whenever a reservation changes
    const standsChannel = supabase.channel('expositor_reservations_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'expositor_reservations'
    }, () => fetchAvailability()).subscribe();
    const toldosChannel = supabase.channel('toldos_reservations_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'toldos_reservations'
    }, () => fetchAvailability()).subscribe();
    return () => {
      supabase.removeChannel(standsChannel);
      supabase.removeChannel(toldosChannel);
    };
  }, []);
  const ftPricePerM2 = 150000; // Updated from 57000
  const ftWidth = parseFloat(ftWidthStr) || 0;
  const ftLength = parseFloat(ftLengthStr) || 0;
  const ftTotalM2 = ftWidth * ftLength;
  const ftTotalPrice = ftTotalM2 * ftPricePerM2;
  // Calculate available toldos with real data + fake floor until 40% (8) sold
  const displayedToldosSold = realToldosSold < 8 ? Math.max(6, realToldosSold) : realToldosSold;
  const toldosAvailable = Math.max(0, TOLDOS_TOTAL - displayedToldosSold);
  const toldosPrice = 300000;
  const toldosTotalPrice = toldosPrice * toldosQuantity;
  const {
    commercialStands,
    foodTruckStands
  } = useMemo(() => {
    const comm: Stand[] = [];
    // Create 36 commercial stands (01-36)
    for (let i = 1; i <= 36; i++) {
      let standType: 'AAA' | 'AA' | 'A';
      let price: number;
      let frentes: string;
      let isLarge: boolean;
      if (AAA_STANDS.includes(i)) {
        standType = 'AAA';
        price = 800000;
        frentes = 'Tres frentes';
        isLarge = true;
      } else if (AA_STANDS.includes(i)) {
        standType = 'AA';
        price = 600000;
        frentes = 'Dos frentes';
        isLarge = true;
      } else {
        standType = 'A';
        price = 500000;
        frentes = 'Un frente';
        isLarge = false;
      }
      const paddedNum = i.toString().padStart(2, '0');
      const id = `${standType}${paddedNum}`;
      const status: StandStatus = occupiedStands[id] === 'paid' ? 'sold' : occupiedStands[id] === 'pending_payment' ? 'reserved' : 'available';
      const row = i <= 12 ? 1 : i <= 24 ? 2 : 3;
      comm.push({
        id,
        status,
        size: '2x2m',
        price,
        type: 'comercial',
        standType,
        frentes,
        isLarge,
        row,
        col: i
      });
    }
    const foodTrucks: Stand[] = [];
    for (let i = 1; i <= 8; i++) {
      const id = `FT${i}`;
      const status: StandStatus = occupiedStands[id] === 'paid' ? 'sold' : occupiedStands[id] === 'pending_payment' ? 'reserved' : 'available';
      foodTrucks.push({
        id,
        status,
        size: 'Variable',
        price: ftPricePerM2,
        type: 'foodtrucks',
        row: i <= 4 ? 1 : 2,
        col: i <= 4 ? i : i - 4
      });
    }
    return {
      commercialStands: comm,
      foodTruckStands: foodTrucks
    };
  }, [ftPricePerM2, occupiedStands]);
  const currentStands = activeTab === 'comercial' ? commercialStands : foodTruckStands;
  const handleStandClick = (stand: Stand) => {
    if (stand.status === 'available') {
      setSelectedStand(stand);
      setIsModalOpen(false); // Close modal when stand selected to show detail panel
    }
  };
  const getCommercialStandClasses = (stand: Stand) => {
    const isSelected = selectedStand?.id === stand.id;
    // AAA stands (super premium) - gold styling
    if (stand.standType === 'AAA') {
      if (stand.status === 'sold') {
        return `rounded-lg border-[3px] flex flex-col items-center justify-center transition-all font-bold relative bg-red-100 border-red-400 text-red-700 cursor-not-allowed opacity-60`;
      }
      if (stand.status === 'reserved') {
        return `rounded-lg border-[3px] flex flex-col items-center justify-center transition-all font-bold relative bg-amber-100 border-amber-400 text-amber-700 cursor-not-allowed opacity-75`;
      }
      return `rounded-lg border-[4px] flex flex-col items-center justify-center transition-all font-bold relative bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-500 text-yellow-900 hover:from-yellow-100 hover:to-amber-100 hover:border-yellow-600 hover:shadow-xl hover:shadow-yellow-400/40 cursor-pointer
        ${isSelected ? 'ring-4 ring-yellow-500 ring-offset-2 scale-110 z-10 shadow-2xl' : ''}`;
    }
    // AA stands (premium) - amber styling
    if (stand.standType === 'AA') {
      if (stand.status === 'sold') {
        return `rounded-lg border-[3px] flex flex-col items-center justify-center transition-all font-bold relative bg-red-100 border-red-400 text-red-700 cursor-not-allowed opacity-60`;
      }
      if (stand.status === 'reserved') {
        return `rounded-lg border-[3px] flex flex-col items-center justify-center transition-all font-bold relative bg-amber-100 border-amber-400 text-amber-700 cursor-not-allowed opacity-75`;
      }
      return `rounded-lg border-[3px] flex flex-col items-center justify-center transition-all font-bold relative bg-white border-amber-400 text-amber-800 hover:bg-amber-50 hover:border-amber-500 hover:shadow-lg hover:shadow-amber-400/30 cursor-pointer
        ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 scale-110 z-10 shadow-xl' : ''}`;
    }
    // A stands (standard) - gray styling
    if (stand.status === 'sold') {
      return `aspect-square rounded border-2 flex items-center justify-center transition-all text-[9px] font-bold bg-red-100 border-red-400 text-red-700 cursor-not-allowed opacity-60`;
    }
    if (stand.status === 'reserved') {
      return `aspect-square rounded border-2 flex items-center justify-center transition-all text-[9px] font-bold bg-amber-100 border-amber-400 text-amber-700 cursor-not-allowed opacity-75`;
    }
    return `aspect-square rounded border-2 flex items-center justify-center transition-all text-[9px] font-bold bg-white border-gray-300 text-gray-700 hover:bg-green-50 hover:border-green-400 cursor-pointer
      ${isSelected ? 'ring-4 ring-brand-cyan ring-offset-2 scale-105' : ''}`;
  };
  const getFoodTruckStandClasses = (stand: Stand) => {
    const isSelected = selectedStand?.id === stand.id;
    const base = 'flex flex-col items-center justify-center transition-all py-3 px-2 rounded-xl';
    if (stand.status === 'sold') {
      return `${base} bg-red-500/20 border-2 border-red-500/40 text-red-300 cursor-not-allowed`;
    }
    if (stand.status === 'reserved') {
      return `${base} bg-red-500/15 border-2 border-red-500/30 text-red-200/80 cursor-not-allowed`;
    }
    return `${base} text-white hover:scale-110 hover:-translate-y-1 cursor-pointer
      ${isSelected ? 'scale-110 -translate-y-1 drop-shadow-[0_0_12px_rgba(249,115,22,0.8)]' : ''}`;
  };
  return <div className="pt-20 bg-brand-navy" data-id="element-2032" data-mp="2zo1oe90idrhz">
      {/* Hero Banner */}
      <section className="relative py-24 bg-brand-navy overflow-hidden" data-id="element-2033" data-mp="33pd51p1en2e1">
        <div className="absolute inset-0" data-id="element-2034" data-mp="33r7ot00xwogj">
          <img src="https://images.unsplash.com/photo-1555244162-803834f70033?w=1920&q=80&auto=format&fit=crop" alt="Feria Bazar" className="w-full h-full object-cover opacity-30" data-id="element-2035" data-mp="38mwttivqaxk8" />
          <div className="absolute inset-0 bg-brand-navy/70" data-id="element-2036" data-mp="3ilcmhqaemevu"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-2037" data-mp="1jabhyvlx2r56">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-cyan hover:text-white transition-colors mb-8 text-sm" data-id="element-2038" data-mp="1o94jdpnxiroz">
            <ArrowLeft className="w-4 h-4" data-id="element-2039" data-mp="miecak2irul" /> Volver al inicio
          </Link>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-5xl md:text-7xl text-white mb-4 font-bold" data-id="element-2040" data-mp="kfd0udgtcak">
            Feria Bazar <span className="text-brand-cyan" data-id="element-2041" data-mp="ob1iqezzykhe">Deportiva</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-xl text-white/80 max-w-2xl mx-auto mb-8" data-id="element-2042" data-mp="kar5ch9vkrsi">
            El espacio perfecto para conectar tu marca con más de 2,000 amantes
            del deporte, el bienestar y las mascotas.
          </motion.p>
          <div className="flex flex-wrap justify-center gap-6 text-white/90" data-id="element-2043" data-mp="mtuj9bca13nv">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-2044" data-mp="mrrj3yhetu5f">
              <Calendar className="w-5 h-5 text-brand-cyan" data-id="element-2045" data-mp="dswf8t1u8w5a" /> 26 Julio 2026
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-2046" data-mp="dr36n87o01un">
              <MapPin className="w-5 h-5 text-brand-green" data-id="element-2047" data-mp="16hlhivemr7im" /> Parque COMFAMA
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-2048" data-mp="16fqth882azab">
              <Store className="w-5 h-5 text-brand-yellow" data-id="element-2049" data-mp="wje3olfn15ku" /> 36 Stands
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Stand Selector */}
      <section className="py-20 bg-white" data-id="element-2050" data-mp="11klcmntioat3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2051" data-mp="3h93gyu0p0jbf">
          <div className="text-center mb-12" data-id="element-2052" data-mp="3hbzh8p00jk31">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4" data-id="element-2053" data-mp="3m9r77dh3kw4l">
              {['Selecciona', 'tu', 'Stand'].map((word, i) => <motion.span key={i} initial={{
              opacity: 0,
              y: 30,
              filter: 'blur(10px)'
            }} whileInView={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: i * 0.15,
              ease: [0.22, 1, 0.36, 1]
            }} className="inline-block shimmer-text mr-3" data-id="element-2054" data-mp="3erb4kl7jvmxo" data-mpi={i}>
                  {word}
                </motion.span>)}
            </h2>
            <p className="text-gray-600" data-id="element-2055" data-mp="2c9565875fkdi">
              Elige la ubicación perfecta para tu marca en nuestro mapa
              interactivo.
            </p>
          </div>

          {/* Tabs - ONLY 2 NOW */}
          <div className="flex justify-center mb-10" data-id="element-2056" data-mp="b88iwng9wxu0">
            <div className="bg-gray-100 p-1.5 rounded-full flex gap-2 max-w-xl w-full" data-id="element-2057" data-mp="g6tgef4553er">
              <button onClick={() => {
              setActiveTab('comercial');
              setSelectedStand(null);
            }} className={`flex-1 py-3 rounded-full font-bold text-sm md:text-base transition-all ${activeTab === 'comercial' ? 'bg-brand-navy text-white shadow-md' : 'text-gray-500 hover:text-brand-navy'}`} data-id="element-2058" data-mp="kxs1rt3zkrov">
                Comercial
              </button>
              <button onClick={() => {
              setActiveTab('foodtrucks');
              setSelectedStand(null);
            }} className={`flex-1 py-3 rounded-full font-bold text-sm md:text-base transition-all ${activeTab === 'foodtrucks' ? 'bg-brand-navy text-white shadow-md' : 'text-gray-500 hover:text-brand-navy'}`} data-id="element-2059" data-mp="270r8o89iv8w">
                Food Trucks - Toldos
              </button>
            </div>
          </div>

          {/* Legend - UPDATED */}
          <div className="flex flex-wrap justify-center gap-6 mb-10" data-id="element-2060" data-mp="2y5t9kooqp20x">
            {!(activeTab === 'foodtrucks' && ftSubTab === 'toldos') && <>
                <div className="flex items-center gap-2" data-id="element-2061" data-mp="2oe5dgah8mwt4">
                  <div className="w-4 h-4 rounded border-2 border-gray-300 bg-white" data-id="element-2062" data-mp="2jg3v5rrmz1ip"></div>
                  <span className="text-sm text-gray-600" data-id="element-2063" data-mp="2jdr48p3whgti">Disponible</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2064" data-mp="2ej4zv5w8koix">
                  <div className="w-4 h-4 rounded border-2 border-amber-400 bg-amber-100" data-id="element-2065" data-mp="2aggux9an81r8"></div>
                  <span className="text-sm text-gray-600" data-id="element-2066" data-mp="25hdxa9wzsxta">Reservado</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2067" data-mp="8n10e1bfq2qz">
                  <div className="w-4 h-4 rounded border-2 border-red-400 bg-red-100" data-id="element-2068" data-mp="3ozi3ilu27gk"></div>
                  <span className="text-sm text-gray-600" data-id="element-2069" data-mp="3n4xk5eh4hd0">Vendido</span>
                </div>
              </>}
            {activeTab === 'comercial' && <>
                <div className="flex items-center gap-2" data-id="element-2070" data-mp="3dq6yjtu5o6bg">
                  <div className="w-5 h-5 rounded-lg bg-gradient-to-br from-yellow-50 to-amber-50 border-[3px] border-yellow-500" data-id="element-2071" data-mp="2yy6jkrn91jgh"></div>
                  <span className="text-sm text-gray-600 font-semibold" data-id="element-2072" data-mp="33w80p71478pb">
                    AAA — Premium 3 frentes
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2073" data-mp="2u16ae7v3s0l5">
                  <div className="w-5 h-5 rounded-lg bg-white border-[3px] border-amber-400" data-id="element-2074" data-mp="2g0c2dqbyw1n0"></div>
                  <span className="text-sm text-gray-600 font-semibold" data-id="element-2075" data-mp="2kzf8zf7lifx3">
                    AA — Premium 2 frentes
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2076" data-mp="e9ig6k7h4m61">
                  <div className="w-4 h-4 rounded bg-white border-2 border-gray-300" data-id="element-2077" data-mp="j8lm06va2wf2"></div>
                  <span className="text-sm text-gray-600" data-id="element-2078" data-mp="23puqzm4gnjut">
                    A — Estándar 1 frente
                  </span>
                </div>
              </>}
            {activeTab === 'foodtrucks' && ftSubTab === 'foodtrucks' && <>
                <div className="flex items-center gap-2" data-id="element-2079" data-mp="2dhqsz89o3u4b">
                  <div className="w-5 h-5 rounded-lg bg-orange-500/30 border-2 border-orange-500/50" data-id="element-2080" data-mp="28ofi8w1qit74"></div>
                  <span className="text-sm text-gray-600 font-semibold" data-id="element-2081" data-mp="2mfaywuih6el9">
                    Food Truck
                  </span>
                </div>
              </>}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10" data-id="element-2082" data-mp="2wdgy34cwd6mf">
            {/* Grid Map */}
            {(() => {
            const PavilionContent = () => <>
                  {activeTab === 'comercial' ? <div className="min-w-[640px]" data-id="element-2083" data-mp="122lmdt6f1bff">
                      {/* Title */}
                      <div className="mb-6 text-center" data-id="element-2084" data-mp="x3qy4bpoiunx">
                        <h3 className="text-2xl font-bold text-[#5fc9d4] tracking-wider" data-id="element-2085" data-mp="2q5y2v60zq9lh">
                          PABELLÓN COMERCIAL
                        </h3>
                        <p className="text-white/60 text-sm mt-2" data-id="element-2086" data-mp="2zwj8veizugef">
                          36 Stands Disponibles
                        </p>
                      </div>

                      {/* Floor Plan Container */}
                      <div className="relative bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-2087" data-mp="277c07mptsafg">
                        <div className="flex gap-4" data-id="element-2088" data-mp="229u10o1m19kn">
                          {/* Left SALIDA labels */}
                          <div className="flex flex-col items-center justify-around" data-id="element-2089" data-mp="2h7cl3d3ceylr">
                            <div className="text-white/70 text-xs font-bold" style={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)'
                      }} data-id="element-2090" data-mp="2c6yocr76zb31">
                              SALIDA
                            </div>
                            <div className="text-white/70 text-xs font-bold" style={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)'
                      }} data-id="element-2091" data-mp="3zv5cr1yet1g">
                              SALIDA
                            </div>
                          </div>

                          <div className="flex-1" data-id="element-2092" data-mp="mpl1pild7mvh">
                            {/* Row 1: 01-06 | SALIDA | 07-12 */}
                            <div className="flex gap-1 mb-4 items-center" data-id="element-2093" data-mp="8wljv9y41gkr">
                              <div className="grid gap-1 flex-1" style={{
                          gridTemplateColumns: 'repeat(6, 1fr)'
                        }} data-id="element-2094" data-mp="cyqbk350034r">
                                {commercialStands.filter(s => s.row === 1 && (s.col ?? 0) <= 6).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getCommercialStandClasses(stand)} style={stand.standType === 'AAA' || stand.standType === 'AA' ? {
                            aspectRatio: '1',
                            minHeight: '100%'
                          } : {
                            aspectRatio: '1'
                          }} data-id="element-2095" data-mp="11cs0kpxksue0">
                                      <span className={stand.standType === 'AAA' || stand.standType === 'AA' ? 'text-[8px] leading-tight text-center' : 'text-[9px]'} data-id="element-2096" data-mp="dg5469d174a8">
                                        {stand.id}
                                      </span>
                                      {stand.standType === 'AAA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 text-yellow-500 text-xs" data-id="element-2097" data-mp="13s088jkpknia">
                                            ★★★
                                          </span>}
                                      {stand.standType === 'AA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-amber-600 shadow-sm" data-id="element-2098" data-mp="1mgnbqxn8l4tr"></span>}
                                    </button>)}
                              </div>
                              <div className="px-1 text-white/70 text-[8px] font-bold text-center leading-tight" style={{
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)'
                        }} data-id="element-2099" data-mp="1rhkrz4oorosm">
                                SALIDA
                              </div>
                              <div className="grid gap-1 flex-1" style={{
                          gridTemplateColumns: 'repeat(6, 1fr)'
                        }} data-id="element-2100" data-mp="1z13u2t9mg8r2">
                                {commercialStands.filter(s => s.row === 1 && (s.col ?? 0) > 6).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getCommercialStandClasses(stand)} style={stand.standType === 'AAA' || stand.standType === 'AA' ? {
                            aspectRatio: '1',
                            minHeight: '100%'
                          } : {
                            aspectRatio: '1'
                          }} data-id="element-2101" data-mp="1l1b6v7xb0anu">
                                      <span className={stand.standType === 'AAA' || stand.standType === 'AA' ? 'text-[8px] leading-tight text-center' : 'text-[9px]'} data-id="element-2102" data-mp="j8zz4zeq8173">
                                        {stand.id}
                                      </span>
                                      {stand.standType === 'AAA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 text-yellow-500 text-xs" data-id="element-2103" data-mp="2l2ys3s9qshae">
                                            ★★★
                                          </span>}
                                      {stand.standType === 'AA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-amber-600 shadow-sm" data-id="element-2104" data-mp="33tgl7xyhlewa"></span>}
                                    </button>)}
                              </div>
                            </div>

                            {/* PASO PEATONAL 1 */}
                            <div className="mb-4 py-3 bg-white/10 rounded-lg border border-white/20" data-id="element-2105" data-mp="2jpnsjdo0q645">
                              <div className="text-center" data-id="element-2106" data-mp="1vztj762wqzon">
                                <div className="text-white font-bold text-sm" data-id="element-2107" data-mp="1w0bb5qijerqr">
                                  PASO PEATONAL
                                </div>
                              </div>
                            </div>

                            {/* Row 2: 13-24 (central, no SALIDA) */}
                            <div className="grid gap-1 mb-4" style={{
                        gridTemplateColumns: 'repeat(12, 1fr)'
                      }} data-id="element-2108" data-mp="1zzk5awhhormu">
                              {commercialStands.filter(s => s.row === 2).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getCommercialStandClasses(stand)} style={stand.standType === 'AAA' || stand.standType === 'AA' ? {
                          aspectRatio: '1',
                          minHeight: '100%'
                        } : {
                          aspectRatio: '1'
                        }} data-id="element-2109" data-mp="o8e1lnjvpkg3">
                                    <span className={stand.standType === 'AAA' || stand.standType === 'AA' ? 'text-[8px] leading-tight text-center' : 'text-[9px]'} data-id="element-2110" data-mp="2y8h84sag7ogj">
                                      {stand.id}
                                    </span>
                                    {stand.standType === 'AAA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 text-yellow-500 text-xs" data-id="element-2111" data-mp="1rtyjce9mj4wn">
                                          ★★★
                                        </span>}
                                    {stand.standType === 'AA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-amber-600 shadow-sm" data-id="element-2112" data-mp="1faugc9pyk2iu"></span>}
                                  </button>)}
                            </div>

                            {/* PASO PEATONAL 2 */}
                            <div className="mb-4 py-3 bg-white/10 rounded-lg border border-white/20" data-id="element-2113" data-mp="11hv4chpdwqg1">
                              <div className="text-center" data-id="element-2114" data-mp="wkutzuli51j4">
                                <div className="text-white font-bold text-sm" data-id="element-2115" data-mp="rlrzv5728fpx">
                                  PASO PEATONAL
                                </div>
                              </div>
                            </div>

                            {/* Row 3: 25-30 | SALIDA | 31-36 */}
                            <div className="flex gap-1 items-center" data-id="element-2116" data-mp="7z709f78ev99">
                              <div className="grid gap-1 flex-1" style={{
                          gridTemplateColumns: 'repeat(6, 1fr)'
                        }} data-id="element-2117" data-mp="cyjl5uejls97">
                                {commercialStands.filter(s => s.row === 3 && (s.col ?? 0) <= 30).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getCommercialStandClasses(stand)} style={stand.standType === 'AAA' || stand.standType === 'AA' ? {
                            aspectRatio: '1',
                            minHeight: '100%'
                          } : {
                            aspectRatio: '1'
                          }} data-id="element-2118" data-mp="hwl66kvj41nb">
                                      <span className={stand.standType === 'AAA' || stand.standType === 'AA' ? 'text-[8px] leading-tight text-center' : 'text-[9px]'} data-id="element-2119" data-mp="1fosxygqmztrv">
                                        {stand.id}
                                      </span>
                                      {stand.standType === 'AAA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 text-yellow-500 text-xs" data-id="element-2120" data-mp="1eozt29il9qmq">
                                            ★★★
                                          </span>}
                                      {stand.standType === 'AA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-amber-600 shadow-sm" data-id="element-2121" data-mp="1tfp4nczyp7r4"></span>}
                                    </button>)}
                              </div>
                              <div className="px-1 text-white/70 text-[8px] font-bold text-center leading-tight" style={{
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)'
                        }} data-id="element-2122" data-mp="3hxiaa5sxfgxr">
                                SALIDA
                              </div>
                              <div className="grid gap-1 flex-1" style={{
                          gridTemplateColumns: 'repeat(6, 1fr)'
                        }} data-id="element-2123" data-mp="5gn3hzmnp3qv">
                                {commercialStands.filter(s => s.row === 3 && (s.col ?? 0) > 30).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getCommercialStandClasses(stand)} style={stand.standType === 'AAA' || stand.standType === 'AA' ? {
                            aspectRatio: '1',
                            minHeight: '100%'
                          } : {
                            aspectRatio: '1'
                          }} data-id="element-2124" data-mp="1cab3pxp0qnx0">
                                      <span className={stand.standType === 'AAA' || stand.standType === 'AA' ? 'text-[8px] leading-tight text-center' : 'text-[9px]'} data-id="element-2125" data-mp="3uhev4oql81o0">
                                        {stand.id}
                                      </span>
                                      {stand.standType === 'AAA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 text-yellow-500 text-xs" data-id="element-2126" data-mp="2wsvetjf4cv71">
                                            ★★★
                                          </span>}
                                      {stand.standType === 'AA' && stand.status === 'available' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-amber-600 shadow-sm" data-id="element-2127" data-mp="2fr7gyrxuj191"></span>}
                                    </button>)}
                              </div>
                            </div>
                          </div>

                          {/* Right SALIDA */}
                          <div className="flex flex-col justify-center" data-id="element-2128" data-mp="1wy3jswlf669o">
                            <div className="text-white/70 text-xs font-bold" style={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)'
                      }} data-id="element-2129" data-mp="21zascxvdz9he">
                              SALIDA
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> :
              // Food Trucks & Toldos
              <div className="min-w-[480px]" data-id="element-2130" data-mp="n26r8611ynkb">
                      {/* Sub-tabs */}
                      <div className="flex justify-center mb-8" data-id="element-2131" data-mp="j0cweedjgds2">
                        <div className="bg-white/20 p-1 rounded-full flex gap-1 w-full max-w-sm border border-white/20" data-id="element-2132" data-mp="ixgxontv9l2k">
                          <button onClick={() => setFtSubTab('foodtrucks')} className={`flex-1 py-2 rounded-full font-bold text-sm transition-all ${ftSubTab === 'foodtrucks' ? 'bg-orange-500 text-white shadow-md' : 'text-white/90 hover:text-white hover:bg-white/10'}`} data-id="element-2133" data-mp="91dtlcfgfccz">
                            Food Trucks
                          </button>
                          <button onClick={() => setFtSubTab('toldos')} className={`flex-1 py-2 rounded-full font-bold text-sm transition-all ${ftSubTab === 'toldos' ? 'bg-amber-500 text-white shadow-md' : 'text-white/90 hover:text-white hover:bg-white/10'}`} data-id="element-2134" data-mp="2mz0ju3itejv7">
                            Toldos
                          </button>
                        </div>
                      </div>

                      {ftSubTab === 'foodtrucks' ? <div data-id="element-2135" data-mp="31nnb93lpv22s">
                          {/* Food Trucks header */}
                          <div className="mb-6 text-center" data-id="element-2136" data-mp="2wuu4ka6otjo2">
                            <h3 className="text-2xl font-bold text-orange-400 tracking-wider" data-id="element-2137" data-mp="2rxka2g4fyhtd">
                              ZONA FOOD TRUCKS
                            </h3>
                            <p className="text-white/60 text-sm mt-2" data-id="element-2138" data-mp="3pe9b47lkhg1p">
                              8 Espacios Disponibles · $150.000/m²
                            </p>
                          </div>
                          <div className="relative bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-2139" data-mp="3rz8ip7duffw6">
                            {/* Top Row */}
                            <div className="grid grid-cols-4 gap-6 mb-8" data-id="element-2140" data-mp="5omgfcd13s03">
                              {foodTruckStands.filter(s => s.row === 1).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getFoodTruckStandClasses(stand)} data-id="element-2141" data-mp="eo9js3pbj7vs">
                                    <img src="https://cdn.magicpatterns.com/uploads/nFXcbyhaDyaXddokgBVZZ4/foodtruck.png" alt="Food Truck" className={`w-20 h-auto mb-2 ${stand.status === 'available' ? 'brightness-0 invert' : 'brightness-0 invert opacity-40'}`} data-id="element-2142" data-mp="yfcwe59vkyop" />
                                    <span className="font-bold text-sm" data-id="element-2143" data-mp="38afp59kgg6kc">
                                      {stand.id}
                                    </span>
                                    {stand.status === 'sold' && <span className="text-[9px] font-bold uppercase tracking-wider mt-0.5" data-id="element-2144" data-mp="3i8vjrmioxxyl">
                                        Vendido
                                      </span>}
                                    {stand.status === 'reserved' && <span className="text-[9px] font-bold uppercase tracking-wider mt-0.5" data-id="element-2145" data-mp="1wtpn82n2e0wj">
                                        Reservado
                                      </span>}
                                  </button>)}
                            </div>
                            {/* Paso peatonal */}
                            <div className="my-6 py-4 bg-white/10 rounded-lg border border-white/20" data-id="element-2146" data-mp="18ye206yaoktw">
                              <div className="text-center" data-id="element-2147" data-mp="1dx7d5sq3zzqw">
                                <div className="text-white font-bold text-sm tracking-widest" data-id="element-2148" data-mp="1dvmkk8ncuqvo">
                                  ZONA DE CIRCULACIÓN Y MESAS
                                </div>
                              </div>
                            </div>
                            {/* Bottom Row */}
                            <div className="grid grid-cols-4 gap-6 mt-8" data-id="element-2149" data-mp="q1l7k7de5dxz">
                              {foodTruckStands.filter(s => s.row === 2).map(stand => <button key={stand.id} onClick={() => handleStandClick(stand)} disabled={stand.status !== 'available'} className={getFoodTruckStandClasses(stand)} data-id="element-2150" data-mp="2pd5tmhnm9o25">
                                    <img src="https://cdn.magicpatterns.com/uploads/nFXcbyhaDyaXddokgBVZZ4/foodtruck.png" alt="Food Truck" className={`w-20 h-auto mb-2 ${stand.status === 'available' ? 'brightness-0 invert' : 'brightness-0 invert opacity-40'}`} data-id="element-2151" data-mp="fe9wfykvuie0" />
                                    <span className="font-bold text-sm" data-id="element-2152" data-mp="3soqxk173k44d">
                                      {stand.id}
                                    </span>
                                    {stand.status === 'sold' && <span className="text-[9px] font-bold uppercase tracking-wider mt-0.5" data-id="element-2153" data-mp="3er27mey6a6sg">
                                        Vendido
                                      </span>}
                                    {stand.status === 'reserved' && <span className="text-[9px] font-bold uppercase tracking-wider mt-0.5" data-id="element-2154" data-mp="23b10xhriak6s">
                                        Reservado
                                      </span>}
                                  </button>)}
                            </div>
                          </div>
                        </div> :
                // Toldos Gastronómicos Section
                <div className="bg-gradient-to-br from-amber-500/20 to-orange-600/20 p-6 sm:p-8 rounded-2xl border-2 border-amber-500/30 shadow-xl relative overflow-hidden" data-id="element-2155" data-mp="1pb8dbvba0k31">
                          {/* Decorative background elements */}
                          <div className="absolute top-0 right-0 -mr-10 -mt-10 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl" data-id="element-2156" data-mp="wrt2vmta03k4"></div>
                          <div className="absolute bottom-0 left-0 -ml-10 -mb-10 w-40 h-40 bg-orange-500/20 rounded-full blur-3xl" data-id="element-2157" data-mp="1v68cww2ywp8a"></div>

                          <div className="relative z-10" data-id="element-2158" data-mp="28wljau07nvvi">
                            <div className="flex items-center gap-4 mb-6" data-id="element-2159" data-mp="24uzzeop9ehbt">
                              <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-3 rounded-xl shadow-lg" data-id="element-2160" data-mp="1h5xjbla9wwjf">
                                <img src="https://cdn.magicpatterns.com/uploads/pBA1b9YRLahkDTRzbjdxcq/toldo.png" alt="Toldo" className="w-8 h-8 brightness-0 invert" data-id="element-2161" data-mp="1c7dv1e87j8nw" />
                              </div>
                              <div data-id="element-2162" data-mp="31k7lbxtsisg9">
                                <h3 className="text-2xl font-black text-white tracking-wide" data-id="element-2163" data-mp="31nllr0iw8rl2">
                                  Toldos Gastronómicos
                                </h3>
                                <p className="text-amber-200/80 text-sm font-medium" data-id="element-2164" data-mp="355sz0lss4ucd">
                                  Ubicación asignada por zonas según
                                  disponibilidad
                                </p>
                              </div>
                            </div>

                            {/* Urgency counter */}
                            <div className="bg-black/20 backdrop-blur-md rounded-xl p-5 mb-5 border border-white/10" data-id="element-2165" data-mp="3nv8zxa8dmotb">
                              <div className="flex justify-between items-end mb-3" data-id="element-2166" data-mp="3ssrcdallrm62">
                                <div data-id="element-2167" data-mp="3e3lcdkexxrx4">
                                  <span className="text-white/80 text-sm font-medium block mb-1" data-id="element-2168" data-mp="3e2k0jdq3p1ec">
                                    Disponibilidad
                                  </span>
                                  <span className="text-amber-400 font-black text-2xl" data-id="element-2169" data-mp="all2u0tqlehe">
                                    {toldosAvailable}{' '}
                                    <span className="text-lg text-white/50 font-medium" data-id="element-2170" data-mp="2bhsnfnhpp273">
                                      de {TOLDOS_TOTAL}
                                    </span>
                                  </span>
                                </div>
                                {toldosAvailable <= 5 && <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-3 py-1 rounded-full text-xs font-bold animate-pulse" data-id="element-2171" data-mp="1xrp7tfdc86qm">
                                    ¡Últimas unidades!
                                  </div>}
                              </div>
                              <div className="w-full bg-black/30 rounded-full h-2.5 overflow-hidden" data-id="element-2172" data-mp="2lhtbfmqj06hm">
                                <div className="bg-gradient-to-r from-amber-500 to-orange-500 h-full rounded-full transition-all duration-500 relative" style={{
                          width: `${toldosAvailable / TOLDOS_TOTAL * 100}%`
                        }} data-id="element-2173" data-mp="2giq41vn2po6h">
                                  <div className="absolute inset-0 bg-white/20 w-full h-full animate-[shimmer_2s_infinite]" data-id="element-2174" data-mp="3lo5799qfopgk"></div>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6" data-id="element-2175" data-mp="3a5x10kg9e5q">
                              {/* Price */}
                              <div className="bg-black/20 backdrop-blur-md rounded-xl p-5 border border-white/10 flex flex-col justify-center" data-id="element-2176" data-mp="87pc22oz1uyq">
                                <p className="text-white/70 text-sm font-medium mb-1" data-id="element-2177" data-mp="d691we6orqwd">
                                  Inversión por toldo
                                </p>
                                <p className="text-3xl font-black text-white" data-id="element-2178" data-mp="2f19jyrtsk8kz">
                                  $300.000{' '}
                                  <span className="text-sm font-medium text-white/50" data-id="element-2179" data-mp="24mtvgxm4tzm3">
                                    + IVA
                                  </span>
                                </p>
                              </div>

                              {/* Quantity selector */}
                              <div className="bg-black/20 backdrop-blur-md rounded-xl p-5 border border-white/10" data-id="element-2180" data-mp="1znqov8qi7lc0">
                                <label className="text-white/90 text-sm font-medium mb-3 block" data-id="element-2181" data-mp="2ndl41wyaplzf">
                                  Cantidad a reservar
                                </label>
                                <div className="flex items-center justify-between gap-3 bg-black/30 rounded-lg p-1 border border-white/5" data-id="element-2182" data-mp="2ecwylqmywty5">
                                  <button onClick={() => setToldosQuantity(Math.max(0, toldosQuantity - 1))} className="w-10 h-10 rounded-md bg-white/5 hover:bg-white/10 text-white font-bold transition-colors flex items-center justify-center disabled:opacity-30" disabled={toldosQuantity <= 0} data-id="element-2183" data-mp="2jaey6qf3hwth">
                                    −
                                  </button>
                                  <input type="number" min="0" max={toldosAvailable} value={toldosQuantity} onChange={e => {
                            const val = parseInt(e.target.value) || 0;
                            setToldosQuantity(Math.min(Math.max(0, val), toldosAvailable));
                          }} className="w-16 text-center bg-transparent border-none px-2 py-2 text-white font-black text-xl focus:outline-none" data-id="element-2184" data-mp="gojdfvl8m0v8" />
                                  <button onClick={() => setToldosQuantity(Math.min(toldosAvailable, toldosQuantity + 1))} className="w-10 h-10 rounded-md bg-white/5 hover:bg-white/10 text-white font-bold transition-colors flex items-center justify-center disabled:opacity-30" disabled={toldosQuantity >= toldosAvailable} data-id="element-2185" data-mp="26ruqbrwol1jk">
                                    +
                                  </button>
                                </div>
                                {toldosQuantity === 0 && <p className="text-amber-300/80 text-xs mt-3 text-center font-medium animate-pulse" data-id="element-2186" data-mp="2zu45zephmcgj">
                                    ↑ Elige cuántos toldos reservar
                                  </p>}
                                {toldosQuantity > 0 && <p className="text-white/60 text-xs mt-3 text-center" data-id="element-2187" data-mp="2lyimfn20wkvi">
                                    Continúa en el panel de la derecha →
                                  </p>}
                              </div>
                            </div>
                          </div>
                        </div>}
                    </div>}
                </>;
            return <>
                  <div className="lg:col-span-2 relative bg-gradient-to-br from-[#0a4a5c] to-[#0d3d4d] p-3 sm:p-6 md:p-10 rounded-3xl border-2 border-[#1a6b7f] overflow-x-auto scroll-smooth shadow-2xl" data-id="element-2188" data-mp="n6e1le9prmft">
                    <p className="sm:hidden text-white/70 text-xs text-center mb-4 animate-pulse" data-id="element-2189" data-mp="d8855c6of8ic">
                      Desliza o toca Ver en grande →
                    </p>
                    <button onClick={() => setIsModalOpen(true)} className="absolute top-4 right-4 z-10 bg-brand-cyan text-white rounded-full px-3 py-2 md:px-4 flex items-center gap-2 shadow-lg hover:bg-blue-600 transition-colors" data-id="element-2190" data-mp="3vk4koo0kc2i4">
                      <Maximize2 className="w-4 h-4" data-id="element-2191" data-mp="3ql1qxzq19ipg" />
                      <span className="hidden md:inline text-sm font-bold" data-id="element-2192" data-mp="3coegqv92kw1l">
                        Ver en grande
                      </span>
                    </button>
                    <PavilionContent data-id="element-2193" data-mp="2y0ef0y10tj67" />
                  </div>

                  {/* Modal */}
                  {isModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/95 backdrop-blur-sm p-4" data-id="element-2194" data-mp="2t2lae9fd8x81">
                      <motion.div initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} className="w-full h-full max-w-6xl max-h-[90vh] bg-gradient-to-br from-[#0a4a5c] to-[#0d3d4d] rounded-3xl border-2 border-[#1a6b7f] shadow-2xl flex flex-col overflow-hidden" data-id="element-2195" data-mp="372nqbq445gkn">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row items-center justify-between p-4 sm:p-6 border-b border-white/10 gap-4 relative" data-id="element-2196" data-mp="3uou7p2zfg8nj">
                          <h2 className="text-xl sm:text-2xl font-bold text-white pr-10 sm:pr-0" data-id="element-2197" data-mp="2xbsr65fw6gg7">
                            {activeTab === 'comercial' ? 'Pabellón Comercial' : 'Food Trucks - Toldos'}
                          </h2>

                          <div className="flex-1 flex justify-center w-full sm:w-auto" data-id="element-2198" data-mp="3b4s3jykdnujj">
                            <div className="bg-white/10 p-1 rounded-full flex gap-1 w-full sm:w-auto" data-id="element-2199" data-mp="3g2k6roqh3331">
                              <button onClick={() => {
                          setActiveTab('comercial');
                          setSelectedStand(null);
                        }} className={`px-4 py-2 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap transition-all ${activeTab === 'comercial' ? 'bg-brand-cyan text-white' : 'text-white/60 hover:text-white'}`} data-id="element-2200" data-mp="3l1n2ujoh5yyv">
                                Comercial
                              </button>
                              <button onClick={() => {
                          setActiveTab('foodtrucks');
                          setSelectedStand(null);
                        }} className={`px-4 py-2 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap transition-all ${activeTab === 'foodtrucks' ? 'bg-brand-cyan text-white' : 'text-white/60 hover:text-white'}`} data-id="element-2201" data-mp="1dgeis4mk73h5">
                                Food Trucks - Toldos
                              </button>
                            </div>
                          </div>

                          <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 sm:relative sm:top-0 sm:right-0 bg-white/10 hover:bg-red-500 text-white p-2 rounded-full transition-colors" data-id="element-2202" data-mp="lq09klphqfbt">
                            <X className="w-6 h-6" data-id="element-2203" data-mp="2kuryeu7edq6h" />
                          </button>
                        </div>

                        {/* Body with Zoom */}
                        <div className="flex-1 overflow-hidden relative bg-[#083846] cursor-move" data-id="element-2204" data-mp="2bssftxgqie1f">
                          <TransformWrapper initialScale={1} minScale={0.5} maxScale={4} centerOnInit={true} wheel={{
                      step: 0.1
                    }} data-id="element-2205" data-mp="2zid1wp86prex">
                            <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full flex items-center justify-center p-8" data-id="element-2206" data-mp="2uq3fhj6a39he">
                              <div className="bg-gradient-to-br from-[#0a4a5c] to-[#0d3d4d] p-8 rounded-3xl border border-[#1a6b7f] shadow-2xl" data-id="element-2207" data-mp="1bsypsdv4svzj">
                                <PavilionContent data-id="element-2208" data-mp="16tvtpix4q03p" />
                              </div>
                            </TransformComponent>
                          </TransformWrapper>
                        </div>
                      </motion.div>
                    </div>}
                </>;
          })()}

            {/* Detail Panel */}
            <div className="lg:col-span-1" data-id="element-2209" data-mp="nwb8cltkpjnu">
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 sticky top-28" data-id="element-2210" data-mp="nxmayiba8ldd">
                {/* Toldos summary panel: takes priority on toldos sub-tab when quantity > 0 */}
                {activeTab === 'foodtrucks' && ftSubTab === 'toldos' && toldosQuantity > 0 ? <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} key={`toldos-${toldosQuantity}`} data-id="element-2211" data-mp="28bmurtfzxef2">
                    <div className="mb-6" data-id="element-2212" data-mp="280y9w1ngos85">
                      <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold mb-3 uppercase tracking-wider" data-id="element-2213" data-mp="27xtzunnbf29k">
                        Listo para reservar
                      </span>
                      <h3 className="text-3xl font-bold text-brand-navy" data-id="element-2214" data-mp="2c2bhoha475hp">
                        {toldosQuantity} Toldo{toldosQuantity > 1 ? 's' : ''}{' '}
                        Gastronómico{toldosQuantity > 1 ? 's' : ''}
                      </h3>
                      <p className="text-gray-500 font-semibold mt-1" data-id="element-2215" data-mp="2lsd1gwvfq1qt">
                        Ubicación asignada por zonas
                      </p>
                    </div>

                    <div className="space-y-2 mb-6 bg-amber-50/60 rounded-xl p-4 border border-amber-100" data-id="element-2216" data-mp="2vr29zz1fy9ue">
                      <div className="flex justify-between text-sm" data-id="element-2217" data-mp="3eekvupthltrk">
                        <span className="text-gray-500" data-id="element-2218" data-mp="3jdeeewnmsayl">Cantidad</span>
                        <span className="font-bold text-brand-navy" data-id="element-2219" data-mp="35ddblbnt2tgh">
                          {toldosQuantity} ×
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-2220" data-mp="3aekk5cxrvwo7">
                        <span className="text-gray-500" data-id="element-2221" data-mp="2vms3mxm8sckw">Precio por toldo</span>
                        <span className="font-bold text-brand-navy" data-id="element-2222" data-mp="30ejxbh0p2yf9">
                          {formatCOP(toldosPrice)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-2223" data-mp="2qo7f3vgcrhca">
                        <span className="text-gray-500" data-id="element-2224" data-mp="2qme649i9ol14">Subtotal</span>
                        <span className="font-medium text-brand-navy" data-id="element-2225" data-mp="2cq0p11hnaln6">
                          {formatCOP(toldosTotalPrice)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-2226" data-mp="n7u4ziir1ka5">
                        <span className="text-gray-500" data-id="element-2227" data-mp="ih3wvxixv3br">IVA (19%)</span>
                        <span className="font-medium text-brand-navy" data-id="element-2228" data-mp="ier4srjgvckz">
                          {formatCOP(priceBreakdown(toldosTotalPrice).iva)}
                        </span>
                      </div>
                      <div className="border-t border-amber-200 pt-2 flex justify-between items-end" data-id="element-2229" data-mp="9e2tw5pddscb">
                        <span className="text-gray-700 font-bold" data-id="element-2230" data-mp="1643pank8v72l">
                          Total a pagar
                        </span>
                        <span className="font-black text-2xl text-orange-600" data-id="element-2231" data-mp="x4yr5iclhfqy">
                          {formatCOP(priceBreakdown(toldosTotalPrice).total)}
                        </span>
                      </div>
                    </div>

                    <div className="mb-8" data-id="element-2232" data-mp="3hxacdrogdb0a">
                      <h4 className="font-bold text-brand-navy mb-3 text-sm uppercase tracking-wider" data-id="element-2233" data-mp="3cx5v3zfq0c90">
                        Cada toldo incluye:
                      </h4>
                      <ul className="space-y-2" data-id="element-2234" data-mp="32r4kbfc0la6t">
                        {['1 Toldo de feria + 1 mesa + 1 silla', '1 conexión 110V (TV / PC / celular)', '2 credenciales de expositor', 'Lleva tu propia exhibición'].map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-2235" data-mp="3lixbd47iwlbw" data-mpi={i}>
                            <Check className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" data-id="element-2236" data-mp="3vio7fabqf8e3" />
                            {item}
                          </li>)}
                      </ul>
                    </div>

                    <button onClick={() => {
                  setRegistrationType('toldo');
                  setIsRegistrationModalOpen(true);
                }} className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-orange-500/30" data-id="element-2237" data-mp="215omr2shvnzy">
                      Reservar {toldosQuantity} toldo
                      {toldosQuantity > 1 ? 's' : ''}
                      <ChevronRight className="w-5 h-5" data-id="element-2238" data-mp="3cw2trpo25b8i" />
                    </button>
                  </motion.div> : selectedStand ? <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} key={selectedStand.id} data-id="element-2239" data-mp="3mm5xokwpw9lm">
                    <div className="flex justify-between items-start mb-6" data-id="element-2240" data-mp="2p5gy6xv8pjfe">
                      <div data-id="element-2241" data-mp="14nxxrfi8twmk">
                        <span className="inline-block px-3 py-1 bg-brand-green/10 text-brand-green rounded-full text-xs font-bold mb-3 uppercase tracking-wider" data-id="element-2242" data-mp="14mmwpng6n2z5">
                          Disponible
                        </span>
                        {selectedStand.standType === 'AAA' && <span className="inline-block px-3 py-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-900 rounded-full text-xs font-bold mb-3 uppercase tracking-wider ml-2" data-id="element-2243" data-mp="10n4b0pgjenv9">
                            ★★★ SUPER PREMIUM
                          </span>}
                        {selectedStand.standType === 'AA' && <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold mb-3 uppercase tracking-wider ml-2" data-id="element-2244" data-mp="1jckmu8xoa2qz">
                            PREMIUM
                          </span>}
                        <h3 className="text-3xl font-bold text-brand-navy" data-id="element-2245" data-mp="7vuccdm7uork">
                          {selectedStand.type === 'foodtrucks' ? 'Food Truck' : 'Stand'}{' '}
                          {selectedStand.id}
                        </h3>
                        <p className="text-gray-500 font-semibold mt-1" data-id="element-2246" data-mp="3b6sdidxvyzjn">
                          {selectedStand.type === 'foodtrucks' ? 'Zona Food Trucks' : 'Pabellón Comercial'}
                        </p>
                      </div>
                    </div>

                    {selectedStand.type === 'foodtrucks' ? <div className="space-y-5 mb-8" data-id="element-2247" data-mp="2sakgfxf11uzf">
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4" data-id="element-2248" data-mp="2nbhbse2ylqrz">
                          <p className="text-sm font-bold text-orange-700 mb-1" data-id="element-2249" data-mp="qj7ffjg6k78n">
                            Precio por m²
                          </p>
                          <p className="text-2xl font-bold text-orange-600" data-id="element-2250" data-mp="lkckxk8uou8p">
                            $150.000
                          </p>
                        </div>

                        <div data-id="element-2251" data-mp="y15c2nd9jnug">
                          <p className="text-sm font-bold text-brand-navy mb-3" data-id="element-2252" data-mp="y03x47t4mh7g">
                            Ingresa las medidas de tu Food Truck:
                          </p>
                          <div className="flex gap-3 items-center" data-id="element-2253" data-mp="17qowhxgmjprf">
                            <div className="flex-1" data-id="element-2254" data-mp="a4rk8hsu8gnx">
                              <label className="text-xs text-gray-500 mb-1 block" data-id="element-2255" data-mp="a6cd82zi7rjo">
                                Ancho (m)
                              </label>
                              <input type="number" min={1} max={10} step={0.5} value={ftWidthStr} onChange={e => setFtWidthStr(e.target.value)} onBlur={() => {
                          const v = parseFloat(ftWidthStr);
                          if (!v || v < 1) setFtWidthStr('1');else if (v > 10) setFtWidthStr('10');
                        }} className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-center font-bold text-brand-navy focus:border-orange-400 focus:outline-none transition-colors" data-id="element-2256" data-mp="k1cpn48rrtsa" />
                            </div>
                            <span className="text-gray-400 font-bold text-lg mt-5" data-id="element-2257" data-mp="2zcwb05gl3las">
                              ×
                            </span>
                            <div className="flex-1" data-id="element-2258" data-mp="3e7140if3snjr">
                              <label className="text-xs text-gray-500 mb-1 block" data-id="element-2259" data-mp="39bc1c67seqj8">
                                Largo (m)
                              </label>
                              <input type="number" min={1} max={10} step={0.5} value={ftLengthStr} onChange={e => setFtLengthStr(e.target.value)} onBlur={() => {
                          const v = parseFloat(ftLengthStr);
                          if (!v || v < 1) setFtLengthStr('1');else if (v > 10) setFtLengthStr('10');
                        }} className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-center font-bold text-brand-navy focus:border-orange-400 focus:outline-none transition-colors" data-id="element-2260" data-mp="1ewj648atg1rt" />
                            </div>
                          </div>
                        </div>

                        <div className="bg-brand-navy/5 rounded-xl p-4 space-y-2" data-id="element-2261" data-mp="w43zi9e651o9">
                          <div className="flex justify-between text-sm" data-id="element-2262" data-mp="111cksium5ltq">
                            <span className="text-gray-500" data-id="element-2263" data-mp="22utp8og8zr63">Área total</span>
                            <span className="font-bold text-brand-navy" data-id="element-2264" data-mp="27teha0lcj4jg">
                              {ftTotalM2.toFixed(2)} m²
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-2265" data-mp="2hj7qww1xh9l2">
                            <span className="text-gray-500" data-id="element-2266" data-mp="2hkivgy376ld8">Precio × m²</span>
                            <span className="font-bold text-brand-navy" data-id="element-2267" data-mp="2mhj2bb7vfs5e">
                              {formatCOP(ftPricePerM2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-2268" data-mp="2vkafpgkb4xzo">
                            <span className="text-gray-500" data-id="element-2269" data-mp="30ilp5q6ox6hd">Subtotal</span>
                            <span className="font-medium text-brand-navy" data-id="element-2270" data-mp="114y3c45ifova">
                              {formatCOP(ftTotalPrice)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-2271" data-mp="ykjlq2cy8p2h">
                            <span className="text-gray-500" data-id="element-2272" data-mp="ylur26m1m4vp">IVA (19%)</span>
                            <span className="font-medium text-brand-navy" data-id="element-2273" data-mp="tt1l5feu8qi1">
                              {formatCOP(priceBreakdown(ftTotalPrice).iva)}
                            </span>
                          </div>
                          <div className="border-t border-gray-200 pt-2 flex justify-between" data-id="element-2274" data-mp="jxgozxwgakg7">
                            <span className="text-gray-700 font-bold" data-id="element-2275" data-mp="1hivxzwz5qf2a">
                              Total a pagar
                            </span>
                            <span className="font-bold text-xl text-orange-600" data-id="element-2276" data-mp="17kyc3g4rsucu">
                              {formatCOP(priceBreakdown(ftTotalPrice).total)}
                            </span>
                          </div>
                        </div>
                      </div> : <div className="space-y-4 mb-8" data-id="element-2277" data-mp="3nbujahcvbjda">
                        <div className="flex justify-between py-3 border-b border-gray-100" data-id="element-2278" data-mp="3negqgfvujwuz">
                          <span className="text-gray-500" data-id="element-2279" data-mp="2fdouqqpv138t">Dimensiones</span>
                          <span className="font-bold text-brand-navy" data-id="element-2280" data-mp="2jd7e3i21969j">
                            {selectedStand.size}
                          </span>
                        </div>
                        <div className="flex justify-between py-3 border-b border-gray-100" data-id="element-2281" data-mp="2ad0zfsulm08s">
                          <span className="text-gray-500" data-id="element-2282" data-mp="1voo2otnosdd8">Frentes</span>
                          <span className="font-bold text-brand-navy" data-id="element-2283" data-mp="20fmmw4bspvlr">
                            {selectedStand.frentes}
                          </span>
                        </div>
                        <div className="flex justify-between py-3 border-b border-gray-100" data-id="element-2284" data-mp="1mmetc745mq1f">
                          <span className="text-gray-500" data-id="element-2285" data-mp="1rl8b4bqh537e">Inversión</span>
                          <span className="font-medium text-brand-navy" data-id="element-2286" data-mp="1cqllv25almqi">
                            {formatCOP(selectedStand.price)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2" data-id="element-2287" data-mp="y44s888qxelt">
                          <span className="text-gray-500 text-sm" data-id="element-2288" data-mp="u29sh86gr9a5">
                            IVA (19%)
                          </span>
                          <span className="font-medium text-brand-navy text-sm" data-id="element-2289" data-mp="1833wzfqe4q3j">
                            {formatCOP(priceBreakdown(selectedStand.price).iva)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-t border-gray-200" data-id="element-2290" data-mp="1gzurvot7hywj">
                          <span className="text-brand-navy font-bold" data-id="element-2291" data-mp="1cyj8i10xe9ws">
                            Total a pagar
                          </span>
                          <span className="font-black text-xl text-brand-navy" data-id="element-2292" data-mp="1lsny6h4bpfaq">
                            {formatCOP(priceBreakdown(selectedStand.price).total)}
                          </span>
                        </div>
                      </div>}

                    <div className="mb-8" data-id="element-2293" data-mp="3p9kgvk5osoe8">
                      <h4 className="font-bold text-brand-navy mb-3 text-sm uppercase tracking-wider" data-id="element-2294" data-mp="3u8noncd1x8pw">
                        Incluye:
                      </h4>
                      <ul className="space-y-2" data-id="element-2295" data-mp="2wcj4nv9abga">
                        {(selectedStand.type === 'foodtrucks' ? ['Espacio para food truck', 'Energía de planta incluida', '2 credenciales de expositor', 'NO incluye punto de agua'] : ['1 conexión 110V (1 TV / 1 computador / 1 celular)', '2 credenciales de expositor', 'Estructura dentro de la carpa', 'Lleva tu propia exhibición (mesas y sillas adicionales bajo solicitud)', 'Ubicación según disponibilidad']).map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-2296" data-mp="2g1rasvf1yxyi" data-mpi={i}>
                            <Check className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" data-id="element-2297" data-mp="26ad4a3oooxvv" />
                            {item}
                          </li>)}
                      </ul>
                    </div>

                    <button onClick={() => {
                  setRegistrationType(selectedStand.type === 'foodtrucks' ? 'foodtruck' : 'comercial');
                  setIsRegistrationModalOpen(true);
                }} className="w-full flex items-center justify-center gap-2 bg-brand-cyan hover:bg-blue-700 text-white py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-cyan-500/30" data-id="element-2298" data-mp="1xcufqpbu01f8">
                      Reservar Espacio
                    </button>
                  </motion.div> : <div className="h-full flex flex-col items-center justify-center text-center py-12 opacity-60" data-id="element-2299" data-mp="1b22hs9wegzv7">
                    <Info className="w-16 h-16 text-gray-400 mb-4" data-id="element-2300" data-mp="1yrwvsusgguh1" />
                    <h3 className="text-xl font-bold text-brand-navy mb-2" data-id="element-2301" data-mp="1ypkbwc350a1c">
                      {activeTab === 'foodtrucks' && ftSubTab === 'toldos' ? 'Selecciona la cantidad' : activeTab === 'foodtrucks' ? 'Ningún espacio seleccionado' : 'Ningún stand seleccionado'}
                    </h3>
                    <p className="text-gray-500" data-id="element-2302" data-mp="dgewak1hg1yg">
                      {activeTab === 'foodtrucks' && ftSubTab === 'toldos' ? 'Elige cuántos toldos quieres reservar a la izquierda y aquí verás el resumen.' : `Haz clic en un ${activeTab === 'foodtrucks' ? 'espacio' : 'stand'} disponible en el mapa para ver sus detalles y reservarlo.`}
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Exhibit Section */}
      <section className="py-20 bg-white" data-id="element-2303" data-mp="2dvq5isv84iwx">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2304" data-mp="2dsap9mptd3tx">
          <div className="text-center mb-16" data-id="element-2305" data-mp="28wvgh96nxy4d">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4" data-id="element-2306" data-mp="2mmyu5brh8ji7">
              ¿Por qué ser expositor?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2307" data-mp="2hv7cjqqato47">
              Únete a la feria más importante de la región y haz crecer tu
              negocio en un ambiente lleno de energía y propósito.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2308" data-mp="1q4u9fjjqj1jv">
            {[{
            icon: Users,
            title: 'Gran Audiencia',
            desc: 'Más de 1,000 visitantes esperados durante todo el día del evento.',
            color: 'text-brand-cyan',
            bg: 'bg-brand-cyan/10'
          }, {
            icon: Store,
            title: 'Visibilidad',
            desc: 'Posicionamiento de marca en un entorno premium y familiar.',
            color: 'text-brand-yellow',
            bg: 'bg-brand-yellow/10'
          }, {
            icon: MessageSquare,
            title: 'Networking',
            desc: 'Conecta con otras marcas líderes del sector bienestar y mascotas.',
            color: 'text-brand-green',
            bg: 'bg-brand-green/10'
          }, {
            icon: TrendingUp,
            title: 'Ventas Directas',
            desc: 'Comercializa tus productos y servicios directamente a tu público objetivo.',
            color: 'text-brand-navy',
            bg: 'bg-brand-navy/10'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow text-center" data-id="element-2309" data-mp="3ltau6sqtvmds" data-mpi={i}>
                <div className={`w-16 h-16 ${item.bg} rounded-2xl flex items-center justify-center mx-auto mb-6`} data-id="element-2310" data-mp="4k4mphhztjda">
                  <item.icon className={`w-8 h-8 ${item.color}`} data-id="element-2311" data-mp="wc2bspwt2gjv" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-2312" data-mp="273vtwaqz87es">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-2313" data-mp="2252egcs4q6bz">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pavilion Info Section - UPDATE: REMOVE GASTRONOMICO, KEEP ONLY 2 CARDS */}
      <section className="py-20 bg-gray-50" data-id="element-2314" data-mp="1o5jj6kkf2ph3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2315" data-mp="1o6kriha1sxv4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-2316" data-mp="1jcgog93wl9zb">
            {/* Comercial */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100" data-id="element-2317" data-mp="1efgevo7uhp3g">
              <div className="flex items-center gap-4 mb-6" data-id="element-2318" data-mp="3c1r28d26lkr3">
                <div className="w-14 h-14 bg-brand-cyan/10 rounded-2xl flex items-center justify-center" data-id="element-2319" data-mp="3g3uidezspdip">
                  <ShoppingBag className="w-7 h-7 text-brand-cyan" data-id="element-2320" data-mp="3l2paj7jnkefh" />
                </div>
                <div data-id="element-2321" data-mp="3q21wlq2p9hh0">
                  <h3 className="text-2xl font-bold text-brand-navy" data-id="element-2322" data-mp="3q18uj2eorg57">
                    Pabellón Comercial
                  </h3>
                  <p className="text-brand-cyan font-medium" data-id="element-2323" data-mp="2sgcsxq406riw">
                    36 Stands Disponibles
                  </p>
                </div>
              </div>
              <p className="text-gray-600 mb-8" data-id="element-2324" data-mp="32boaeg6sqcuc">
                Ideal para marcas de ropa deportiva, accesorios para mascotas,
                suplementos, tecnología y servicios de bienestar. Ubicado en la
                zona de mayor tráfico peatonal.
              </p>
              <div className="space-y-4" data-id="element-2325" data-mp="2emvcie6m5ux3">
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2326" data-mp="2elk7kb1fmh4e">
                  <span className="text-gray-500" data-id="element-2327" data-mp="26kw2u3tid366">
                    Stand A (1 frente, 2x2m)
                  </span>
                  <span className="font-bold text-brand-navy" data-id="element-2328" data-mp="22ird3g66kqot">$500.000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2329" data-mp="2gecw96pqgg9b">
                  <span className="text-gray-500" data-id="element-2330" data-mp="2bf8o8jejookk">
                    Stand AA (2 frentes, 2x2m)
                  </span>
                  <span className="font-bold text-brand-navy" data-id="element-2331" data-mp="1jlzmelob79aq">$600.000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2332" data-mp="1xlr6n45f5lx6">
                  <span className="text-gray-500" data-id="element-2333" data-mp="1smo0thhm7bo5">
                    Stand AAA (3 frentes, 2x2m)
                  </span>
                  <span className="font-bold text-brand-navy" data-id="element-2334" data-mp="3904pfulbfb6c">$800.000</span>
                </div>
              </div>
            </motion.div>

            {/* Food Trucks & Toldos */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100" data-id="element-2335" data-mp="pubonkiha4o3">
              <div className="flex items-center gap-4 mb-6" data-id="element-2336" data-mp="19i1894g9gym8">
                <div className="w-14 h-14 bg-orange-500/10 rounded-2xl flex items-center justify-center" data-id="element-2337" data-mp="19ikqyndqjr1b">
                  <Truck className="w-7 h-7 text-orange-500" data-id="element-2338" data-mp="1diuwjyxm8ffn" />
                </div>
                <div data-id="element-2339" data-mp="zrq5vmc86xhg">
                  <h3 className="text-2xl font-bold text-brand-navy" data-id="element-2340" data-mp="zq5axvm01cj2">
                    Food Trucks & Toldos
                  </h3>
                  <p className="text-orange-500 font-medium" data-id="element-2341" data-mp="1sgdlb1aamsn3">
                    8 Food Trucks + 20 Toldos
                  </p>
                </div>
              </div>
              <p className="text-gray-600 mb-8" data-id="element-2342" data-mp="1x9ghziadai7i">
                Espacios diseñados para food trucks y toldos gastronómicos.
                Excelente ubicación cerca a la tarima principal y zonas de alto
                tráfico.
              </p>
              <div className="space-y-4" data-id="element-2343" data-mp="3vrjvop784oss">
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2344" data-mp="3vsmmyy8gkt98">
                  <span className="text-gray-500" data-id="element-2345" data-mp="3qtifqd53h5lj">Food Truck (variable)</span>
                  <span className="font-bold text-brand-navy" data-id="element-2346" data-mp="rbskcthu6ovr">$150.000/m²</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2347" data-mp="nb8lnlhm7d7i">
                  <span className="text-gray-500" data-id="element-2348" data-mp="iifih21socxj">Toldo Gastronómico</span>
                  <span className="font-bold text-brand-navy" data-id="element-2349" data-mp="djuh2zat3n7i">$300.000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-2350" data-mp="1b1cp9suszcyi">
                  <span className="text-gray-500" data-id="element-2351" data-mp="168jlb775s8nh">Energía de planta</span>
                  <span className="font-bold text-brand-green" data-id="element-2352" data-mp="1dkzaihly0azz">Incluido</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Summary - UPDATE: REMOVE GASTRONOMICO, UPDATE PRICES */}
      <section className="py-24 bg-brand-navy relative overflow-hidden" data-id="element-2353" data-mp="1nhlwj9q0983l">
        <div className="absolute inset-0 bg-paw-pattern-white opacity-5 pointer-events-none" data-id="element-2354" data-mp="1rla2xp2whxn9"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2355" data-mp="1wisc9gktyevs">
          <div className="text-center mb-16" data-id="element-2356" data-mp="21hwjw2s3w4k0">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2357" data-mp="21gliuaq1pawl">
              Resumen de Inversión
            </h2>
            <p className="text-white/70" data-id="element-2358" data-mp="2b8828ucjegaf">
              Opciones adaptadas a las necesidades de tu marca
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8" data-id="element-2359" data-mp="2fb4gy9yyhs93">
            {/* Stand A */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all" data-id="element-2360" data-mp="2k9peu2qqjzud">
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2361" data-mp="p3ws7736e7ng">Stand A</h3>
              <p className="text-gray-300 mb-6" data-id="element-2362" data-mp="13tceoavobqxc">2×2m · 1 frente</p>
              <div className="mb-8" data-id="element-2363" data-mp="ywm0jt3fj5ek">
                <span className="text-4xl font-black text-white" data-id="element-2364" data-mp="yvjb7plr9b0r">$500.000</span>
                <span className="text-white/50 text-sm" data-id="element-2365" data-mp="1cuaml498gil7"> + IVA</span>
              </div>
              <ul className="space-y-3 mb-8" data-id="element-2366" data-mp="17xk7aj595r0u">
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2367" data-mp="17uekjak3j18a">
                  <Check className="w-4 h-4 text-brand-cyan" data-id="element-2368" data-mp="3engumymluski" /> Dentro de carpa
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2369" data-mp="39pf6w0e8i52p">
                  <Check className="w-4 h-4 text-brand-cyan" data-id="element-2370" data-mp="1p74l001cu0xb" /> 1 Conexión 110V
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2371" data-mp="1u3l8cs0qcb9c">
                  <Check className="w-4 h-4 text-brand-cyan" data-id="element-2372" data-mp="1geb2ltqm2lf8" /> 2 Credenciales
                </li>
              </ul>
            </motion.div>

            {/* Stand AA */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-amber-500/20 backdrop-blur-sm rounded-3xl p-8 border-2 border-amber-400" data-id="element-2373" data-mp="16fbqu2aju9o0">
              <div className="absolute top-0 right-0 bg-amber-400 text-amber-900 px-4 py-1 rounded-bl-xl text-xs font-bold" data-id="element-2374" data-mp="2rtg4wwmtyhyb">
                PREMIUM
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2375" data-mp="1jjv9m6bpxjrz">Stand AA</h3>
              <p className="text-amber-300 mb-6" data-id="element-2376" data-mp="1ela905sk0y30">2×2m · 2 frentes</p>
              <div className="mb-8" data-id="element-2377" data-mp="1eo67bv8bdorz">
                <span className="text-4xl font-black text-white" data-id="element-2378" data-mp="19ubqczk3fnwl">$600.000</span>
                <span className="text-white/50 text-sm" data-id="element-2379" data-mp="14wa5c933xeih"> + IVA</span>
              </div>
              <ul className="space-y-3 mb-8" data-id="element-2380" data-mp="10uf5za4qlb7c">
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2381" data-mp="10v6rtt8j7ukf">
                  <Check className="w-4 h-4 text-amber-400" data-id="element-2382" data-mp="vv2kottikc6v" /> Ubicación esquina
                </li>
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2383" data-mp="qvfwxqg27djt">
                  <Check className="w-4 h-4 text-amber-400" data-id="element-2384" data-mp="lxef18uddk9x" /> Doble frente
                </li>
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2385" data-mp="2la0cjopiwiwc">
                  <Check className="w-4 h-4 text-amber-400" data-id="element-2386" data-mp="2l7wbdej0dxzr" /> 2 Credenciales
                </li>
              </ul>
            </motion.div>

            {/* Stand AAA */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-gradient-to-br from-yellow-500/30 to-amber-500/30 backdrop-blur-sm rounded-3xl p-8 border-2 border-yellow-500 relative" data-id="element-2387" data-mp="2orodc8sj2ph0">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-900 px-4 py-1 rounded-bl-xl text-xs font-bold" data-id="element-2388" data-mp="29x0028gvymqu">
                ★★★ SUPER PREMIUM
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2389" data-mp="2jqqnrb56ut5h">Stand AAA</h3>
              <p className="text-yellow-300 mb-6" data-id="element-2390" data-mp="3q8xbj3u88rbh">2×2m · 3 frentes</p>
              <div className="mb-8" data-id="element-2391" data-mp="3v7773fjv5txm">
                <span className="text-4xl font-black text-white" data-id="element-2392" data-mp="3vacu8p8xmlqp">$800.000</span>
                <span className="text-white/50 text-sm" data-id="element-2393" data-mp="20jay886nijv5"> + IVA</span>
              </div>
              <ul className="space-y-3 mb-8" data-id="element-2394" data-mp="24j30k35gbts2">
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2395" data-mp="1qszgniu1oc5s">
                  <Check className="w-4 h-4 text-yellow-400" data-id="element-2396" data-mp="1qqvfh8nj5r97" /> Ubicación
                  central
                </li>
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2397" data-mp="1gus5jgmfsobp">
                  <Check className="w-4 h-4 text-yellow-400" data-id="element-2398" data-mp="1gwwieo2bcxo4" /> Triple frente
                </li>
                <li className="flex items-center gap-2 text-white/90 text-sm" data-id="element-2399" data-mp="1lrsblrjadr2t">
                  <Check className="w-4 h-4 text-yellow-400" data-id="element-2400" data-mp="17voloafg44w5" /> Máxima
                  visibilidad
                </li>
              </ul>
            </motion.div>

            {/* Food Trucks */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all" data-id="element-2401" data-mp="328o5ygurtn9r">
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2402" data-mp="3lb6sa5ar6p88">Food Trucks</h3>
              <p className="text-orange-400 mb-6" data-id="element-2403" data-mp="3v71edg0stw8">Variable</p>
              <div className="mb-8" data-id="element-2404" data-mp="3v2jiytktf9co">
                <span className="text-4xl font-black text-white" data-id="element-2405" data-mp="3v1hxdv66b4gp">$150.000</span>
                <span className="text-white/50 text-sm" data-id="element-2406" data-mp="cxg9yxrqad6z"> /m² + IVA</span>
              </div>
              <ul className="space-y-3 mb-8" data-id="element-2407" data-mp="8vmf5647s3eq">
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2408" data-mp="8vuporcv6wmr">
                  <Check className="w-4 h-4 text-orange-400" data-id="element-2409" data-mp="2epckq562nj0p" /> Espacio para
                  vehículo
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2410" data-mp="2aoixle0ge88v">
                  <Check className="w-4 h-4 text-orange-400" data-id="element-2411" data-mp="dndedyo47ybu" /> Energía de
                  planta
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2412" data-mp="ilexgjljjxnb">
                  <Check className="w-4 h-4 text-orange-400" data-id="element-2413" data-mp="njgg53f21uy9" /> 2 Credenciales
                </li>
              </ul>
            </motion.div>

            {/* Toldos */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all" data-id="element-2414" data-mp="wlhbtvun48xt">
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2415" data-mp="1ka0jqa9s2jpd">Toldos</h3>
              <p className="text-brand-green mb-6" data-id="element-2416" data-mp="1k85uwkvdy7g0">Gastronómico</p>
              <div className="mb-8" data-id="element-2417" data-mp="34u495h1z4ryj">
                <span className="text-4xl font-black text-white" data-id="element-2418" data-mp="30qz8lk1gf0cj">$300.000</span>
                <span className="text-white/50 text-sm" data-id="element-2419" data-mp="30t2ap2fvsjxm"> + IVA</span>
              </div>
              <ul className="space-y-3 mb-8" data-id="element-2420" data-mp="2vs4tot6lxvxp">
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2421" data-mp="2qtbduu3ulsud">
                  <Check className="w-4 h-4 text-brand-green" data-id="element-2422" data-mp="3ogk4t4mmbu3r" /> Toldo + mesa +
                  silla
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2423" data-mp="3jgxi84kwh1ia">
                  <Check className="w-4 h-4 text-brand-green" data-id="element-2424" data-mp="3eqqt83mh7ezi" /> 1 Conexión 110V
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm" data-id="element-2425" data-mp="39rnqwqxvrmv8">
                  <Check className="w-4 h-4 text-brand-green" data-id="element-2426" data-mp="39pjjhz0rw5q7" /> 2 Credenciales
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gray-50" data-id="element-2427" data-mp="3mybgmtoylsa1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2428" data-mp="3mx8tmwurc9ze">
          <h2 className="text-3xl font-bold text-brand-navy mb-4" data-id="element-2429" data-mp="3r0evlsau7f02">
            ¿Necesitas un montaje especial?
          </h2>
          <p className="text-gray-600 mb-10" data-id="element-2430" data-mp="3d1ffxa5ayag1">
            Si tu marca requiere un espacio mayor a 16m² o un diseño
            arquitectónico personalizado (Free Design), nuestro equipo comercial
            está listo para asesorarte.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2431" data-mp="eii9h5gipt5n">
            <a href="https://wa.me/573000000000" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-green-600 text-white px-8 py-4 rounded-full font-bold transition-colors shadow-lg" data-id="element-2432" data-mp="ekkybm5xpg8s">
              <MessageSquare className="w-5 h-5" data-id="element-2433" data-mp="x8pxshfk6f1s" /> Contactar por WhatsApp
            </a>
            <a href="mailto:comercial@latidoyhuella.com" className="inline-flex items-center justify-center gap-2 bg-white border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white px-8 py-4 rounded-full font-bold transition-colors" data-id="element-2434" data-mp="2hzew2jl05mef">
              <Mail className="w-5 h-5" data-id="element-2435" data-mp="2qtjkyxgksnrb" /> Enviar Correo
            </a>
          </div>
        </div>
      </section>

      <ExpositorRegistrationModal open={isRegistrationModalOpen} onClose={() => setIsRegistrationModalOpen(false)} type={registrationType} selectedStand={selectedStand} price={registrationType === 'toldo' ? toldosPrice : selectedStand?.type === 'foodtrucks' ? ftPricePerM2 : selectedStand?.price || 0} quantity={toldosQuantity} ftDimensions={{
      width: ftWidth,
      length: ftLength,
      totalM2: ftTotalM2
    }} data-id="element-2436" data-mp="1z099xf4jgqs9" />
    </div>;
}
// Helper icon for corner stands
function StarIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2437" data-mp="2h2yc71hb9wt3">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" data-id="element-2438" data-mp="2q2v1n5biyzp7" />
    </svg>;
}